import {
  ContactCandidates,
  ContactEnrichments,
  EliminatedPerson,
  EnrichedContact,
  EnrichmentConfidence,
  PeopleCandidate,
  PeopleRole,
  useApolloQuota,
  useLead,
  useOperation,
  usePeopleDiscovery,
  usePeopleDiscoveryTrace,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import CollapsibleCell from "./CollapsibleCell";
import { mergeContactEnrichments, mergePeopleRoleResults } from "./poolContacts";

// Mirror of the BE `_company_name_from_decision`: pull the chosen company name
// out of a role decision, tolerating the nested auto-pick shape and the flat one.
const companyFromDecision = (
  decision: Record<string, unknown> | null
): string => {
  if (!decision) return "";
  const payload =
    typeof decision.payload === "object" && decision.payload !== null
      ? (decision.payload as Record<string, unknown>)
      : {};
  for (const source of [payload, decision]) {
    for (const key of ["displayName", "orgName", "companyName"]) {
      const raw = source[key];
      if (typeof raw === "string" && raw.trim()) return raw.trim();
    }
  }
  return "";
};

// The AI ranker's priority bands, best-first, with the operator-facing label.
const PRIORITY_ORDER: { band: string; label: string }[] = [
  { band: "primary", label: "Primary target" },
  { band: "secondary", label: "Also strong" },
  { band: "sustainability", label: "Sustainability / ESG" },
  { band: "other", label: "Other" },
];
const bandRank = (band: string): number => {
  const i = PRIORITY_ORDER.findIndex((p) => p.band === band);
  return i === -1 ? PRIORITY_ORDER.length : i;
};
const bandLabel = (band: string): string =>
  PRIORITY_ORDER.find((p) => p.band === band)?.label ?? "Other";

const fullName = (p: { firstName: string; lastNameMasked: string }): string =>
  `${p.firstName} ${p.lastNameMasked}`.trim() || "(no name)";

// How many name/position rows the collapsed cell previews before "+N more".
const PREVIEW_LIMIT = 8;

// A role with a chosen company but no contacts yet may have a discovery running
// server-side that this cell didn't kick off - the promote handler auto-runs free
// discovery, and a landlord/tenant company change re-runs it, but the inbox has no op
// to poll. So briefly poll the lead when a role is in that state (the first fetch
// catches an already-landed run; the polls catch one still in flight), bounded so a
// role that genuinely has no people stops after ~a minute.
const AUTO_DISCOVERY_CHECK_LIMIT = 10;
const AUTO_DISCOVERY_CHECK_INTERVAL_MS = 6000;

type ChipStyle = { label: string; bg: string; fg: string };

// Email-confidence chip: the BE confidence bucket (persisted on each record by
// confidence_from_status) as a coloured label. Never a fabricated percentage - just the
// vendor signal. "undeliverable" is the BE's known-bad bucket (bounced/unavailable/
// invalid); it must NOT read as a mailable grey "Unverified", so it gets a red danger
// chip. The FE renders the persisted bucket directly - the BE owns the mapping, so the
// two surfaces agree by construction (no client-side re-derivation from the raw status).
const CONFIDENCE_CHIP: Record<EnrichmentConfidence, ChipStyle> = {
  verified: { label: "Verified", bg: "#d1e7dd", fg: "#0f5132" },
  likely: { label: "Likely", bg: "#fff3cd", fg: "#664d03" },
  unverified: { label: "Unverified", bg: "#e9ecef", fg: "#495057" },
  undeliverable: { label: "Undeliverable", bg: "#f8d7da", fg: "#842029" },
  none: { label: "No email", bg: "#e9ecef", fg: "#6c757d" },
};

const ConfidenceChip: React.FC<{ contact: EnrichedContact }> = ({
  contact,
}) => {
  const chip = CONFIDENCE_CHIP[contact.confidence] ?? CONFIDENCE_CHIP.none;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 999,
        padding: "2px 9px",
        whiteSpace: "nowrap",
        background: chip.bg,
        color: chip.fg,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "currentColor",
          opacity: 0.85,
        }}
      />
      {chip.label}
    </span>
  );
};

// One Contacts cell (landlord or tenant) in the leads right-panel grid, styled
// like the sibling Ownership cells. Collapsed: the company + an x/y contact
// count + a name/position preview, with the Find button in the footer. Expanded
// (full-width): the searches that ran, the ranked table + the excluded list (each
// with a select column), a "Pull contact info" action for the ticked people, and
// the AI trace. The pulled contacts land in the SEPARATE ContactDetailsCell below.
export const ContactsRoleCell: React.FC<{
  leadId: string;
  role: PeopleRole;
  decision: Record<string, unknown> | null;
  contactCandidates: ContactCandidates | null;
  contactEnrichments: ContactEnrichments | null;
  expanded: boolean;
  onToggle: () => void;
  // Full-width row span - used when it's the sole Contacts cell (owner-occupier,
  // where only the occupier/tenant contacts are shown).
  span?: boolean;
  // Pool this cell's discovery with another role's (owner-occupier: the one company is
  // ranked under both personas, so the tenant cell pools in the landlord persona's people
  // deduped by apolloId). null = no pooling (the normal per-role cell). Required so a
  // caller can't silently forget it (see the "required typed props" rule).
  poolWithRole: PeopleRole | null;
}> = ({
  leadId,
  role,
  decision,
  contactCandidates,
  contactEnrichments,
  expanded,
  onToggle,
  span,
  poolWithRole,
}) => {
  const { findLeadPeople, pullContactInfo } = usePeopleDiscovery();
  const queryClient = useQueryClient();
  const [op, setOp] = useState<{ key: string; siteId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which AI trace the modal is showing (the ranking pass or the domain-resolution pass),
  // by run_id + title; null when closed. Two separate traces, one shared viewer.
  const [traceModal, setTraceModal] = useState<{
    runId: string;
    title: string;
  } | null>(null);
  const [showEliminated, setShowEliminated] = useState(false);
  // Optional operator-typed domain - handed to Apollo for an exact org match when
  // the auto name search misses (e.g. a small company Apollo indexes under a domain).
  const [domainInput, setDomainInput] = useState("");
  // Ephemeral selection of apolloIds to pull (both ranked + excluded rows). Only the
  // PULL RESULTS persist; the tick state itself is local and clears after a pull.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pullOp, setPullOp] = useState<{ key: string; siteId: string } | null>(
    null
  );
  const [pullMessage, setPullMessage] = useState<string | null>(null);
  const [pullError, setPullError] = useState<string | null>(null);

  const companyName = companyFromDecision(decision);
  const hasCompany = companyName.length > 0;

  const primaryListBlob = contactCandidates?.[role] ?? null;
  // Pooled (owner-occupier) or plain: merging with a null "other" is the identity, so a
  // non-pooling cell (poolWithRole=null) sees exactly `primaryListBlob`.
  const listRoleBlob = mergePeopleRoleResults(
    primaryListBlob,
    poolWithRole ? (contactCandidates?.[poolWithRole] ?? null) : null
  );
  // The inbox /leads list omits each role's heavy `eliminated` array (payload bloat -
  // only rendered in the collapsed "excluded" section). Fetch the single lead to fill it
  // in, but ONLY for a role that actually COMPLETED a ranking - the only status carrying
  // an `eliminated` list. A marker blob (not_run / quota_exhausted / no_people) has none,
  // so a detail GET for it is pure waste; without this gate a bulk promote's not_run tail
  // would fire one GET per un-run lead across the inbox (an N+1 the markers introduce).
  // The list-sourced blob paints the count + preview immediately; the detail fetch fills
  // in `eliminated`. The completion effect below invalidates ["lead", leadId] to refetch.
  const hasRoleDiscovery = listRoleBlob?.status === "complete";
  // Auto-discovery follow-up (see AUTO_DISCOVERY_* above): when a role has a company but
  // NO blob yet (discovery genuinely unresolved) and nothing was kicked off from this
  // cell, fetch + briefly poll the lead so an auto-kicked run appears without a reload. A
  // present blob - a result OR a not_run/throttled marker - means the auto-run already
  // settled, so there is nothing to wait for (and no detail GET to fire).
  const [autoChecks, setAutoChecks] = useState(0);
  const autoPollEnabled =
    hasCompany &&
    // Gate on the PRIMARY role's own blob, not the pooled one: an OO tenant that never
    // ran should still trigger its own auto-discovery poll even if the landlord ran.
    primaryListBlob === null &&
    op === null &&
    autoChecks < AUTO_DISCOVERY_CHECK_LIMIT;
  const leadDetail = useLead(
    hasRoleDiscovery || autoPollEnabled ? leadId : undefined
  );
  const roleResult = mergePeopleRoleResults(
    leadDetail.data?.lead.contactCandidates?.[role] ?? primaryListBlob,
    poolWithRole
      ? (leadDetail.data?.lead.contactCandidates?.[poolWithRole] ??
          contactCandidates?.[poolWithRole] ??
          null)
      : null
  );
  // The PRIMARY role's own (unpooled) discovery - what this cell's Find/Pull actually
  // target. Auto-poll and pull-selection key off THIS, not the pooled `roleResult`, so a
  // pooled cell still waits for / acts on the primary role's own data.
  const primaryOwnBlob =
    leadDetail.data?.lead.contactCandidates?.[role] ?? primaryListBlob;
  // Gate on the primary's OWN blob (not the pooled roleResult): an OO tenant whose own
  // discovery hasn't landed should still auto-poll even if the landlord persona already
  // populated the pooled view.
  const awaitingAutoDiscovery = autoPollEnabled && primaryOwnBlob === null;
  // The apolloIds the primary role actually discovered (candidates OR eliminated). The
  // pull endpoint validates ids against contactCandidates[role] (this cell's `role`), so
  // a person present ONLY under the pooled-in persona can't be pulled from here - it would
  // be dropped server-side. Selection is therefore offered only for ids in this set; for a
  // non-pooled cell it contains every rendered id, so nothing changes.
  const primaryKnownIds = new Set<string>([
    ...(primaryOwnBlob?.candidates ?? []).map((c) => c.apolloId),
    ...(primaryOwnBlob?.eliminated ?? [])
      .map((p) => p.apolloId)
      .filter((x): x is string => !!x),
  ]);
  useEffect(() => {
    if (!awaitingAutoDiscovery) return;
    // Re-schedules each tick (autoChecks changes); invalidate via the stable
    // queryClient rather than the per-render leadDetail object so the timeout isn't
    // reset every render. The first fetch already fired when useLead enabled.
    const t = setTimeout(() => {
      setAutoChecks((n) => n + 1);
      void queryClient.invalidateQueries(["lead", leadId]);
    }, AUTO_DISCOVERY_CHECK_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [awaitingAutoDiscovery, autoChecks, queryClient, leadId]);

  const candidates: PeopleCandidate[] = [...(roleResult?.candidates ?? [])].sort(
    (a, b) => bandRank(a.band) - bandRank(b.band) || a.modelOrder - b.modelOrder
  );
  const eliminated: EliminatedPerson[] = roleResult?.eliminated ?? [];
  // The raw "Searches run" log only earns its place when it shows something the "Domain
  // used" headline + result count don't: a fallback chain (a rejected domain then a name
  // search) or a name search (whose exact query differs from the company name). A single
  // successful domain search is fully captured by "Domain used", so hide it as noise.
  const searchAttempts = roleResult?.attempts ?? [];
  const searchLogRedundant =
    searchAttempts.length === 1 &&
    searchAttempts[0].kind === "domain" &&
    searchAttempts[0].query === roleResult?.domain;
  // A throttle marker (Apollo daily/rate limit) persisted with no candidates - show
  // "throttled - retry" rather than an empty result that reads as "found nobody".
  const throttled =
    roleResult?.status === "quota_exhausted" && candidates.length === 0;
  // A not_run marker: the sequential bulk auto-run never reached this pair (past its
  // cap / soft budget, or the shared daily quota ran out). Persisted with no candidates
  // so the cell reads "not run yet - click Find people" rather than an empty result that
  // reads as "found nobody".
  const notRun = roleResult?.status === "not_run" && candidates.length === 0;

  // The apolloIds already pulled for this role (freshest of the single-lead fetch and
  // the list prop). Used to mark done rows and to compute how many credits a pull will
  // actually spend (already-pulled rows are skipped server-side, no re-charge).
  const enrichResult = mergeContactEnrichments(
    leadDetail.data?.lead.contactEnrichments?.[role] ??
      contactEnrichments?.[role] ??
      null,
    poolWithRole
      ? (leadDetail.data?.lead.contactEnrichments?.[poolWithRole] ??
          contactEnrichments?.[poolWithRole] ??
          null)
      : null
  );
  const enrichedIds = new Set(
    (enrichResult?.contacts ?? []).map((c) => c.apolloId)
  );

  // Live remaining enrichment credits (fetched only while expanded). null when Apollo
  // doesn't expose the balance on this plan - the hint then drops "of remaining Y".
  const apolloQuota = useApolloQuota(expanded);
  const creditsLeft = apolloQuota.data?.enrichmentCredits?.left ?? null;

  const selectedArr = [...selectedIds];
  // Ticked people that aren't already pulled AND are in the primary role's own discovery
  // (the pull is keyed on `role`, so a pooled-in-only id would be dropped server-side).
  const chargeable = selectedArr.filter(
    (id) => !enrichedIds.has(id) && primaryKnownIds.has(id)
  );

  const toggleSelect = (apolloId: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(apolloId)) next.delete(apolloId);
      else next.add(apolloId);
      return next;
    });

  const opPoll = useOperation(op?.key ?? null, op?.siteId ?? null);
  const status = opPoll.data?.operation?.status;
  const running =
    op !== null &&
    !["completed", "failed", "cancelled"].includes(status ?? "");
  const progressMessage = opPoll.data?.operation?.progress?.message;

  useEffect(() => {
    if (!op) return;
    const operation = opPoll.data?.operation;
    if (!operation) return;
    if (operation.status === "completed") {
      const result = operation.result ?? {};
      const resultStatus =
        typeof result.status === "string" ? result.status : "";
      if (resultStatus === "not_configured") {
        setError("Apollo People Search isn't configured on this environment.");
      } else if (resultStatus === "no_company") {
        setError(
          `No ${role} company chosen for this lead - pick one before finding people.`
        );
      } else if (resultStatus === "quota_exhausted") {
        setError("Apollo's daily discovery quota is used up - try tomorrow.");
      } else if (resultStatus === "no_people") {
        setError(
          `Apollo found ${companyName} but no matching people in the UK.`
        );
      }
      // Refetch the lead so contactCandidates (and this cell) update. The BE no
      // longer bumps the lead's updatedAt on a people write, so this updates the
      // result in place without remounting (and collapsing) the cell.
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["lead", leadId]);
      opPoll.remove();
      setOp(null);
    } else if (
      operation.status === "failed" ||
      operation.status === "cancelled"
    ) {
      setError("Finding people failed - see the operations page.");
      setOp(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opPoll.data?.operation?.status]);

  const pullOpPoll = useOperation(pullOp?.key ?? null, pullOp?.siteId ?? null);
  const pullStatus = pullOpPoll.data?.operation?.status;
  const pullRunning =
    pullOp !== null &&
    !["completed", "failed", "cancelled"].includes(pullStatus ?? "");
  const pullProgress = pullOpPoll.data?.operation?.progress?.message;

  useEffect(() => {
    if (!pullOp) return;
    const operation = pullOpPoll.data?.operation;
    if (!operation) return;
    if (operation.status === "completed") {
      const result = operation.result ?? {};
      const rs = typeof result.status === "string" ? result.status : "";
      if (rs === "not_configured") {
        setPullError("Apollo enrichment isn't configured on this environment.");
      } else if (rs === "quota_exhausted") {
        // Covers BOTH a pre-gate empty balance AND a mid-run 429; the BE puts the
        // specific reason in result.error, so prefer it (mirrors stale_company below).
        // A genuinely empty balance must not read as a transient "try again shortly".
        setPullError(
          typeof result.error === "string"
            ? result.error
            : "Apollo rate/credit limit hit mid-pull - try again shortly."
        );
      } else if (rs === "no_candidates") {
        setPullError("No discovered people to pull for this role yet.");
      } else if (rs === "stale_company") {
        // The operator changed the company mid-pull, so the reveals were discarded
        // (they belonged to the old company). Not a success - say so plainly.
        setPullError(
          typeof result.error === "string"
            ? result.error
            : "The company changed while pulling - those contacts were discarded."
        );
      } else if (rs === "error") {
        setPullError("Pulling contact info failed - try again.");
      } else {
        const enriched = Number(result.enrichedCount ?? 0);
        const skipped = Number(result.skippedCount ?? 0);
        const missing = Number(result.missingCount ?? 0);
        const credits = Number(result.creditsConsumed ?? 0);
        const parts = [`Pulled ${enriched} contact${enriched === 1 ? "" : "s"}`];
        if (missing) parts.push(`${missing} with no match`);
        if (skipped) parts.push(`${skipped} already had details`);
        parts.push(`${credits} credit${credits === 1 ? "" : "s"} used`);
        setPullMessage(parts.join(" · "));
        // Clear the ticks ONLY on a successful pull. On a failure/partial (quota hit
        // mid-run, stale company, error) keep them so the operator can retry exactly
        // the ids left un-attempted - a re-pull skips the ids already pulled.
        setSelectedIds(new Set());
      }
      // Refresh the lead (details tile + done markers) + the credit balance.
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["lead", leadId]);
      queryClient.invalidateQueries(["apolloQuota"]);
      pullOpPoll.remove();
      setPullOp(null);
    } else if (
      operation.status === "failed" ||
      operation.status === "cancelled"
    ) {
      setPullError("Pulling contact info failed - see the operations page.");
      setPullOp(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullOpPoll.data?.operation?.status]);

  const handleFind = async () => {
    setError(null);
    try {
      const res = await findLeadPeople.mutateAsync({
        leadId,
        role,
        domainOverride: domainInput.trim() || undefined,
      });
      setOp({ key: res.operationKey, siteId: res.siteId });
    } catch {
      setError("Could not start the people search. Try again.");
    }
  };

  const handlePull = async () => {
    setPullError(null);
    setPullMessage(null);
    try {
      const res = await pullContactInfo.mutateAsync({
        leadId,
        role,
        apolloIds: chargeable,
      });
      if (res.deduped) {
        // An identical pull for this lead + role was already in flight, so the BE
        // coalesced onto it instead of starting a second one - that running pull may
        // not include this selection, so these ids could go unpulled. Tell the
        // operator to re-run once it finishes (the ticks are kept, so it is one
        // click). We don't poll the other pull's op here - its result is not this
        // selection's.
        setPullError(
          `A contact pull for this ${role} is already running for this lead - your selection may not be included. Wait for it to finish, then click "Pull contact info" again.`
        );
        return;
      }
      setPullOp({ key: res.operationKey, siteId: res.siteId });
    } catch {
      setPullError("Could not start the contact pull. Try again.");
    }
  };

  const kicking = findLeadPeople.isLoading && op === null;
  const pullKicking = pullContactInfo.isLoading && pullOp === null;
  const pullBusy = pullRunning || pullKicking;

  // The select cell shown at the head of each people row: a "done" tick for an
  // already-pulled person (not re-selectable - re-pull is skip-only), otherwise a
  // checkbox. A legacy row without an apolloId (discovered before this shipped) is
  // simply not selectable.
  const selectCell = (apolloId: string | undefined) => {
    if (!apolloId) return <td />;
    if (enrichedIds.has(apolloId)) {
      return (
        <td
          className="text-center"
          title="Contact info already pulled"
          style={{ color: "#0f5132", fontWeight: 700 }}
        >
          ✓
        </td>
      );
    }
    if (!primaryKnownIds.has(apolloId)) {
      // Pooled in from the landlord ranking (owner-occupier) but absent from the tenant
      // role's own discovery, so it can't be pulled from this cell (the pull keys on the
      // tenant role). Shown - not hidden - just not selectable here.
      return (
        <td
          className="text-center text-muted"
          title="Found under the landlord ranking - not pullable from the pooled view"
          style={{ fontSize: 11 }}
        >
          –
        </td>
      );
    }
    return (
      <td className="text-center">
        <Form.Check
          type="checkbox"
          checked={selectedIds.has(apolloId)}
          onChange={() => toggleSelect(apolloId)}
          disabled={pullBusy}
          style={{ margin: 0, minHeight: 0 }}
          aria-label={`Select ${apolloId} to pull contact info`}
        />
      </td>
    );
  };

  const hasSelectablePeople = candidates.length > 0 || eliminated.length > 0;

  const summary = hasCompany ? (
    <div style={{ fontSize: 12.5 }}>
      <span style={{ fontWeight: 600 }}>{companyName}</span>
      {roleResult &&
        (throttled ? (
          <span style={{ color: "#b26a00", fontWeight: 600 }}>
            {" · "}
            Throttled - retry
          </span>
        ) : notRun ? (
          <span style={{ color: "#6c757d", fontWeight: 600 }}>
            {" · "}
            Not run yet - click Find people
          </span>
        ) : (
          <span style={{ color: "#6c757d" }}>
            {" · "}
            {candidates.length}/{roleResult.apolloTotalEntries} contacts
          </span>
        ))}
      {!expanded && candidates.length > 0 && (
        <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0 }}>
          {candidates.slice(0, PREVIEW_LIMIT).map((p) => (
            <li
              key={p.apolloId}
              style={{
                display: "flex",
                gap: 8,
                padding: "3px 0",
                borderTop: "1px solid #f0f1f3",
                fontSize: 12,
              }}
            >
              <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {fullName(p)}
              </span>
              <span
                style={{
                  color: "#6c757d",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {p.title}
              </span>
            </li>
          ))}
          {candidates.length > PREVIEW_LIMIT && (
            <li style={{ color: "#9aa2ad", fontSize: 11.5, padding: "3px 0" }}>
              +{candidates.length - PREVIEW_LIMIT} more
            </li>
          )}
        </ul>
      )}
    </div>
  ) : (
    <span style={{ color: "#6c757d", fontSize: 12.5 }}>
      (choose a {role} first)
    </span>
  );

  const footer = (
    <>
      <div
        className="d-flex align-items-center gap-2 flex-wrap"
        style={{ marginTop: 8 }}
      >
        {hasCompany && (
          <Form.Control
            type="text"
            size="sm"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="optional domain (e.g. company.co.uk)"
            disabled={running || kicking}
            style={{ maxWidth: 240, fontSize: 12 }}
          />
        )}
        <Button
          size="sm"
          variant="outline-primary"
          disabled={!hasCompany || running || kicking}
          onClick={() => void handleFind()}
        >
          {running || kicking ? (
            <>
              <Spinner size="sm" animation="border" className="me-1" />
              {progressMessage ?? "Finding people..."}
            </>
          ) : roleResult && !notRun ? (
            "Re-find people (free)"
          ) : (
            "Find people (free)"
          )}
        </Button>
        {awaitingAutoDiscovery && !running && !kicking && (
          // Non-blocking hint while we poll for an auto-kicked run (on promote or a
          // company change); the Find button stays clickable in case the operator
          // wants to trigger it themselves.
          <span
            className="text-muted d-inline-flex align-items-center"
            style={{ fontSize: 12 }}
          >
            <Spinner size="sm" animation="border" className="me-1" />
            Checking for contacts...
          </span>
        )}
      </div>
      {error && (
        <Alert variant="warning" className="mt-2 mb-1 py-1 px-2">
          {error}
        </Alert>
      )}
    </>
  );

  return (
    <>
      <CollapsibleCell
        title={role}
        summary={summary}
        footer={footer}
        expanded={expanded}
        onToggle={onToggle}
        span={span}
      >
        {roleResult ? (
          <>
            <div
              className="text-muted d-flex align-items-center gap-2 flex-wrap"
              style={{ fontSize: 12 }}
            >
              <span>
                {throttled ? (
                  <span style={{ color: "#b26a00", fontWeight: 600 }}>
                    Throttled at{" "}
                    {new Date(roleResult.discoveredAt).toLocaleString()}
                  </span>
                ) : notRun ? (
                  <span style={{ color: "#6c757d", fontWeight: 600 }}>
                    Not run yet - the bulk contacts run didn&apos;t reach this one
                  </span>
                ) : (
                  <>
                    {candidates.length} people from{" "}
                    {roleResult.apolloTotalEntries} at {roleResult.companyName}
                    {roleResult.sampled && " (sampled)"} -{" "}
                    {new Date(roleResult.discoveredAt).toLocaleString()}
                  </>
                )}
              </span>
              {/* Only offer the button when a ranking trace exists (its run_id is on the
                  role blob). A throttle / not_run marker / no_people run stores none. */}
              {!throttled && !notRun && roleResult.traceRunId && (
                <Button
                  variant="link"
                  className="p-0"
                  style={{ fontSize: 12, verticalAlign: "baseline" }}
                  onClick={() =>
                    setTraceModal({
                      runId: roleResult.traceRunId!,
                      title: `Contact ranking - AI trace (${role})`,
                    })
                  }
                >
                  view contact ranking trace
                </Button>
              )}
            </div>

            {roleResult.domain && (
              <div className="mt-1" style={{ fontSize: 12 }}>
                <span style={{ color: "#0a7d55", fontWeight: 600 }}>
                  Domain used: {roleResult.domain}
                </span>
                {roleResult.domainSource === "ai" && (
                  <span className="text-muted">
                    {" (found by AI"}
                    {roleResult.domainTraceRunId && (
                      <>
                        {" - "}
                        <Button
                          variant="link"
                          className="p-0"
                          style={{ fontSize: 12, verticalAlign: "baseline" }}
                          onClick={() =>
                            setTraceModal({
                              runId: roleResult.domainTraceRunId!,
                              title: `Domain resolution - AI trace (${role})`,
                            })
                          }
                        >
                          view trace
                        </Button>
                      </>
                    )}
                    {")"}
                  </span>
                )}
                {roleResult.domainSource === "captured" && (
                  <span className="text-muted"> (from the website on file)</span>
                )}
                {roleResult.domainSource === "override" && (
                  <span className="text-muted"> (you entered this)</span>
                )}
              </div>
            )}

            {searchAttempts.length > 0 && !searchLogRedundant && (
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                {searchAttempts.length > 1 ? "Searches run: " : "Searched: "}
                {searchAttempts
                  .map((a) =>
                    a.kind === "domain"
                      ? `domain ${a.query}: ${a.totalEntries}`
                      : `name "${a.query}": ${a.totalEntries}`
                  )
                  .join(" · ")}
              </div>
            )}

            {candidates.length === 0 ? (
              <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                {throttled
                  ? "Apollo's daily discovery quota was used up when this ran - retry to search again."
                  : notRun
                    ? 'The bulk contacts run didn’t reach this one - click "Find people (free)" below to run it now.'
                    : "No people to show."}
              </div>
            ) : (
              <Table
                size="sm"
                className="mt-2 mb-1 align-middle"
                style={{ fontSize: 12 }}
              >
                <thead>
                  <tr className="text-muted">
                    <th style={{ width: 26 }} />
                    <th>Name</th>
                    <th>Position</th>
                    <th>Priority</th>
                    <th>Reasoning</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((person) => (
                    <tr key={person.apolloId}>
                      {selectCell(person.apolloId)}
                      <td className="fw-semibold text-nowrap">
                        {fullName(person)}
                      </td>
                      <td>{person.title}</td>
                      <td className="text-nowrap">{bandLabel(person.band)}</td>
                      <td className="text-muted">{person.reasoning}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {eliminated.length > 0 && (
              <div className="mt-1">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => setShowEliminated((v) => !v)}
                >
                  {showEliminated ? "Hide" : "Show"} {eliminated.length} excluded
                  by the AI ranking
                </Button>
                {showEliminated && (
                  <Table
                    size="sm"
                    className="mt-1 mb-1 align-middle"
                    style={{ fontSize: 12 }}
                  >
                    <thead>
                      <tr className="text-muted">
                        <th style={{ width: 26 }} />
                        <th>Name</th>
                        <th>Position</th>
                        <th>Why excluded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eliminated.map((p, i) => (
                        <tr key={p.apolloId ?? `${p.firstName}-${p.title}-${i}`}>
                          {selectCell(p.apolloId)}
                          <td className="text-nowrap">{fullName(p)}</td>
                          <td>{p.title}</td>
                          <td className="text-muted">{p.reasoning || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            )}

            {/* Pull action - BELOW both tables so it clearly applies to everyone
                ticked (ranked + excluded). Reveals email/title/LinkedIn for the new
                selections; already-pulled rows are skipped (no re-charge). */}
            {hasSelectablePeople && (
              <div
                className="d-flex align-items-center gap-2 flex-wrap"
                style={{ marginTop: 10 }}
              >
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={chargeable.length === 0 || pullBusy}
                  onClick={() => void handlePull()}
                >
                  {pullBusy ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-1" />
                      {pullProgress ?? "Pulling contact info..."}
                    </>
                  ) : (
                    "Pull contact info"
                  )}
                </Button>
                {!pullBusy && chargeable.length > 0 && (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    uses {chargeable.length}{" "}
                    {creditsLeft != null
                      ? `of remaining ${creditsLeft} `
                      : ""}
                    Apollo credit{chargeable.length === 1 ? "" : "s"}
                  </span>
                )}
                {!pullBusy &&
                  chargeable.length === 0 &&
                  selectedArr.length > 0 && (
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      all selected already pulled
                    </span>
                  )}
                {/* Success confirmation: small + inline (right of the button), and
                    dismissable. It's cleared at the start of the next pull, so a fresh
                    run always overwrites the previous one. */}
                {!pullBusy && pullMessage && (
                  <span
                    className="d-inline-flex align-items-center"
                    style={{ fontSize: 12, color: "#0f5132" }}
                  >
                    {pullMessage}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-1"
                      style={{
                        fontSize: 14,
                        lineHeight: 1,
                        color: "#9aa2ad",
                        textDecoration: "none",
                      }}
                      onClick={() => setPullMessage(null)}
                      title="Dismiss"
                      aria-label="Dismiss confirmation"
                    >
                      ×
                    </Button>
                  </span>
                )}
              </div>
            )}
            {pullError && (
              <Alert variant="warning" className="mt-2 mb-1 py-1 px-2">
                {pullError}
              </Alert>
            )}
          </>
        ) : (
          <div className="text-muted" style={{ fontSize: 13 }}>
            No contacts yet - use &quot;Find people (free)&quot; below.
          </div>
        )}
      </CollapsibleCell>

      <PeopleTraceModal
        leadId={leadId}
        role={role}
        openTrace={traceModal}
        onHide={() => setTraceModal(null)}
      />
    </>
  );
};

// The pulled-contacts tile: a SEPARATE cell that sits beneath its role's people
// cell (tenant under tenant, landlord under landlord). Lists the revealed contacts -
// Name (links to LinkedIn) / Title / Email / Confidence chip - each with a disabled
// "Draft email" button (wired up in a future pass). Empty until the first pull.
export const ContactDetailsCell: React.FC<{
  role: PeopleRole;
  contactEnrichments: ContactEnrichments | null;
  expanded: boolean;
  onToggle: () => void;
  // Full-width row span - used when it's the sole details cell (owner-occupier).
  span?: boolean;
  // Pool this cell's pulled contacts with another role's (owner-occupier), deduped by
  // apolloId - mirrors ContactsRoleCell so the done-ticks above and the details below
  // always agree. null = no pooling. Required (see the "required typed props" rule).
  poolWithRole: PeopleRole | null;
}> = ({ role, contactEnrichments, expanded, onToggle, span, poolWithRole }) => {
  const enrichResult = mergeContactEnrichments(
    contactEnrichments?.[role] ?? null,
    poolWithRole ? (contactEnrichments?.[poolWithRole] ?? null) : null
  );
  const contacts: EnrichedContact[] = enrichResult?.contacts ?? [];
  const companyName = enrichResult?.companyName ?? null;
  // Every ticked person gets a record (matched or missed) so skip-only can re-skip it,
  // so `attempted` = how many we tried, `withEmail` = how many came back with an email,
  // and `pulled` = the ones Apollo actually matched (what the preview lists).
  const attempted = contacts.length;
  const withEmail = contacts.filter((c) => !!c.email).length;
  const pulled = contacts.filter((c) => c.matched);

  // Mirror the people tile above: company + a "N/M" count, then a name/title preview
  // when collapsed. The full detail table lives in the expanded body below.
  const summary =
    attempted > 0 ? (
      <div style={{ fontSize: 12.5 }}>
        {companyName && <span style={{ fontWeight: 600 }}>{companyName}</span>}
        <span style={{ color: "#6c757d" }}>
          {companyName ? " · " : ""}
          {withEmail}/{attempted} with email
        </span>
        {!expanded && pulled.length > 0 && (
          <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0 }}>
            {pulled.slice(0, PREVIEW_LIMIT).map((c) => (
              <li
                key={c.apolloId}
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "3px 0",
                  borderTop: "1px solid #f0f1f3",
                  fontSize: 12,
                }}
              >
                <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                  {c.name}
                </span>
                <span
                  style={{
                    color: "#6c757d",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.title}
                </span>
              </li>
            ))}
            {pulled.length > PREVIEW_LIMIT && (
              <li style={{ color: "#9aa2ad", fontSize: 11.5, padding: "3px 0" }}>
                +{pulled.length - PREVIEW_LIMIT} more
              </li>
            )}
          </ul>
        )}
      </div>
    ) : (
      <span style={{ fontSize: 12.5, color: "#9aa2ad" }}>
        No contacts pulled yet
      </span>
    );

  return (
    <CollapsibleCell
      title={`${role} · contact details`}
      summary={summary}
      expanded={expanded}
      onToggle={onToggle}
      span={span}
    >
      {contacts.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 13 }}>
          Tick people in the {role} tile above, then use &quot;Pull contact
          info&quot;.
        </div>
      ) : (
        <Table
          size="sm"
          className="mt-1 mb-1 align-middle"
          style={{ fontSize: 12 }}
        >
          <thead>
            <tr className="text-muted">
              <th>Name</th>
              <th>Title</th>
              <th>Email</th>
              <th>Confidence</th>
              <th style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.apolloId}>
                <td className="fw-semibold text-nowrap">
                  {c.linkedinUrl ? (
                    <a
                      href={c.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open LinkedIn profile"
                    >
                      {c.name}
                      <span
                        style={{
                          fontSize: 8.5,
                          fontWeight: 700,
                          color: "#fff",
                          background: "#0a66c2",
                          borderRadius: 3,
                          padding: "0 3px",
                          marginLeft: 5,
                          verticalAlign: "middle",
                        }}
                      >
                        in
                      </span>
                    </a>
                  ) : (
                    c.name
                  )}
                </td>
                <td>{c.title || "-"}</td>
                <td>
                  {c.email ? (
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>
                      {c.email}
                    </span>
                  ) : (
                    <span className="text-muted">- no email found</span>
                  )}
                </td>
                <td>
                  <ConfidenceChip contact={c} />
                </td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled
                    title="Coming in a later pass"
                    style={{ whiteSpace: "nowrap", fontSize: 11 }}
                  >
                    Draft email
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </CollapsibleCell>
  );
};

const PeopleTraceModal: React.FC<{
  leadId: string;
  role: PeopleRole;
  // The trace to show (run_id + modal title), or null when closed. One viewer serves
  // both the contact-ranking trace and the domain-resolution trace.
  openTrace: { runId: string; title: string } | null;
  onHide: () => void;
}> = ({ leadId, role, openTrace, onHide }) => {
  const traceQuery = usePeopleDiscoveryTrace(
    `${leadId}:${role}`,
    openTrace?.runId ?? null,
    openTrace !== null
  );
  return (
    <Modal show={openTrace !== null} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{openTrace?.title ?? "AI trace"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {traceQuery.isLoading ? (
          <Spinner animation="border" />
        ) : traceQuery.data ? (
          <AITraceViewer trace={traceQuery.data} />
        ) : (
          <div className="text-muted">No AI trace available.</div>
        )}
      </Modal.Body>
    </Modal>
  );
};
